import { useEffect, useRef, type ElementType } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Flex,
  Icon,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from '@chakra-ui/react';
import { VolumeMax } from '@kula-ai/icons/outline/VolumeMax';
import { VolumeX } from '@kula-ai/icons/outline/VolumeX';
import { RefreshCw01 } from '@kula-ai/icons/outline/RefreshCw01';
import { RefreshCcw01 } from '@kula-ai/icons/outline/RefreshCcw01';
import { Maximize01 } from '@kula-ai/icons/outline/Maximize01';
import { FaPlay, FaPause } from 'react-icons/fa';

import { VideoSection } from '@/components/shared/interviews-revamp/interview-transcript/VideoSection';
import {
  selectTimeToJump,
  selectToPlay,
} from '@/store/interview-video/selectors';
import { updateCurrentTime } from '@/store/interview-video';
import {
  setIsPlaying,
  setIsMute,
  setPlaybackSpeed,
  setProgress,
  setDuration,
} from '@/store/interview-video';
import {
  selectIsPlaying,
  selectIsMute,
  selectPlaybackSpeed,
  selectProgress,
  selectDuration,
  selectCurrentTime,
} from '@/store/interview-video/selectors';
import { KText } from '@/ui-library/components/text';
import { useHotkeys } from 'react-hotkeys-hook';

interface VideoPlayerControlProps {
  videoUrl: string;
  onMaximize?: () => void;
  mode?: 'mini' | 'modal';
  onPlayerInteraction?: () => void;
}

export const VideoPlayerControl = ({
  videoUrl,
  onMaximize,
  mode = 'mini',
  onPlayerInteraction,
}: VideoPlayerControlProps): JSX.Element => {
  const dispatch = useDispatch();
  const isPlaying = useSelector(selectIsPlaying);
  const isMute = useSelector(selectIsMute);
  const playbackSpeed = useSelector(selectPlaybackSpeed);
  const progress = useSelector(selectProgress);
  const duration = useSelector(selectDuration);
  const toPlay = useSelector(selectToPlay);
  const timeJumpValue = useSelector(selectTimeToJump);
  const currentTime = useSelector(selectCurrentTime);

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = currentTime || 0;
      if (isPlaying) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (videoRef.current) {
      // Set duration once the metadata is loaded
      videoRef.current.onloadedmetadata = () => {
        dispatch(setDuration(videoRef?.current?.duration || 0));
      };

      // Update progress as the video plays
      videoRef.current.ontimeupdate = () => {
        dispatch(setProgress(videoRef?.current?.currentTime || 0));
      };

      // Set the play button when the video ends
      videoRef.current.onended = () => {
        dispatch(setIsPlaying(false));
      };
    }
  }, [videoRef]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (toPlay) {
      videoRef?.current?.play();
      dispatch(setIsPlaying(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeJumpValue]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMute;
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [isMute, playbackSpeed]);

  const handleTime = (): void => {
    const currentTime = videoRef.current?.currentTime;
    if (currentTime !== undefined) {
      dispatch(updateCurrentTime(currentTime));
    }
  };

  const togglePlayPause = (): void => {
    onPlayerInteraction?.();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      dispatch(setIsPlaying(!isPlaying));
    }
  };

  const skipBack = (): void => {
    onPlayerInteraction?.();
    if (videoRef.current) {
      videoRef.current.currentTime -= 5;
    }
  };

  const skipForward = (): void => {
    onPlayerInteraction?.();
    if (videoRef.current) {
      videoRef.current.currentTime += 5;
    }
  };

  const handleVolumeChange = (): void => {
    onPlayerInteraction?.();
    if (videoRef.current) {
      dispatch(setIsMute(!isMute));
      videoRef.current.muted = !isMute;
    }
  };

  const handleProgressChange = (value: number): void => {
    onPlayerInteraction?.();
    if (videoRef.current) {
      videoRef.current.currentTime = value;
      dispatch(setProgress(value));
    }
  };

  const handlePlaybackSpeed = (): void => {
    onPlayerInteraction?.();
    if (videoRef.current) {
      const newSpeed = playbackSpeed >= 2 ? 1 : playbackSpeed + 0.25;
      dispatch(setPlaybackSpeed(newSpeed));
      videoRef.current.playbackRate = newSpeed;
    }
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  useHotkeys('left', skipBack);
  useHotkeys('right', skipForward);

  const videoWrapperStyles =
    mode === 'mini'
      ? {
          w: '75px',
          right: '100px',
          alignItems: 'center',
          justifyContent: 'center',
          maxH: '43px',
          overflow: 'hidden',
          borderRadius: '8px',
        }
      : {
          w: '100%',
          h: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        };

  return (
    <>
      <Flex
        flex={1}
        flexDir="column"
        bg="secondary-25"
        data-test-id="video-player-control"
      >
        <Flex
          flex="1"
          justifyContent="center"
          alignItems="center"
          p="14px 22px"
          gap="18px"
        >
          <KText
            role="presentation"
            size="sm"
            cursor={'pointer'}
            minW="38px"
            textAlign="right"
            onClick={handlePlaybackSpeed}
          >
            {playbackSpeed}X
          </KText>
          <Flex gap="6px" alignItems="center">
            <Flex
              pos="relative"
              alignItems="center"
              justifyContent="center"
              onClick={skipBack}
              cursor="pointer"
            >
              <Icon as={RefreshCcw01} w="25px" h="25px" />
              <KText fontSize="10px" pos="absolute" mt="2px" ml="1px">
                5
              </KText>
            </Flex>
            <Flex
              bg="primary-500"
              w="36px"
              h="36px"
              borderRadius="50%"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              onClick={togglePlayPause}
            >
              {!isPlaying ? (
                <Icon
                  as={FaPlay as ElementType}
                  w="12px"
                  h="12px"
                  color="base-white"
                />
              ) : (
                <Icon
                  as={FaPause as ElementType}
                  w="12px"
                  h="12px"
                  color="base-white"
                />
              )}
            </Flex>
            <Flex
              pos="relative"
              alignItems="center"
              justifyContent="center"
              onClick={skipForward}
              cursor="pointer"
            >
              <Icon as={RefreshCw01} w="25px" h="25px" />
              <KText fontSize="10px" pos="absolute" mt="2px" ml="1px">
                5
              </KText>
            </Flex>
          </Flex>
          <Flex
            {...videoWrapperStyles}
            position="unset"
            _hover={{ '> div': { visibility: 'visible', opacity: 1 } }}
            cursor="pointer"
            onClick={onMaximize}
          >
            {mode === 'mini' && (
              <Flex
                w="28px"
                h="28px"
                pos="absolute"
                bg="#000"
                alignItems="center"
                justifyContent="center"
                borderRadius="6px"
                cursor="pointer"
                zIndex={999}
                visibility="hidden"
                opacity={0}
                transition="opacity 0.3s ease-in-out"
                onClick={onMaximize}
              >
                <Icon as={Maximize01} color="#fff" w="16px" h="16px" />
              </Flex>
            )}
            <VideoSection
              videoRef={videoRef}
              videoUrl={videoUrl}
              handleTime={handleTime}
              timeJumpValue={timeJumpValue}
              mode={mode}
            />
          </Flex>
          <Flex onClick={handleVolumeChange} cursor="pointer">
            {!isMute ? (
              <Icon as={VolumeMax} w="16px" h="16px" />
            ) : (
              <Icon as={VolumeX} w="16px" h="16px" />
            )}
          </Flex>
        </Flex>
        <Flex pb="20px" px="22px" alignItems="center">
          <KText flex={1}>{formatTime(progress)}</KText>
          <Flex flex={10}>
            <Slider
              aria-label="slider-ex-1"
              value={progress}
              max={duration}
              onChange={handleProgressChange}
              focusThumbOnChange={false}
            >
              <SliderTrack bg="secondary-100" h="6px">
                <SliderFilledTrack bg="primary-500" />
              </SliderTrack>
              <SliderThumb
                border="1px solid"
                borderColor="border-color"
                w="12px"
                h="12px"
              />
            </Slider>
          </Flex>
          <KText flex={1} textAlign="right">
            {formatTime(duration)}
          </KText>
        </Flex>
      </Flex>
    </>
  );
};
